import {
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, finalize, tap } from 'rxjs';

import { isCartSave } from '@core/helpers/cart-save-request.helper';
import { CartSaveStatusService } from '@core/services/cart-save-status.service';

/** Feeds {@link CartSaveStatusService} from the cart's own save traffic (US 21.13). */
@Injectable()
export class CartSaveStatusInterceptor implements HttpInterceptor {
  private readonly saveStatus = inject(CartSaveStatusService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!isCartSave(req)) return next.handle(req);

    this.saveStatus.begin();

    // Success is taken from the response event, NOT from the stream completing: every caller goes
    // through `BaseService`'s `firstValueFrom`, which unsubscribes on the first value and so never
    // lets a `complete` through. Settling there left a failure latched forever, because the
    // unsubscribe fell to `finalize` below, which cannot tell a finished save from a cancelled one.
    //
    // `finalize` stays as the backstop for a request that really is cancelled — it reports no
    // outcome at all — and the guard keeps the pair to one settlement each.
    let settled = false;
    const settle = (report: () => void): void => {
      if (settled) return;
      settled = true;
      report();
    };

    return next.handle(req).pipe(
      tap({
        next: (event) => {
          if (event.type === HttpEventType.Response) {
            settle(() => this.saveStatus.settle(true));
          }
        },
        error: () => settle(() => this.saveStatus.settle(false)),
      }),
      finalize(() => settle(() => this.saveStatus.abandon())),
    );
  }
}
